import { PayloadAction, createSelector, createSlice } from '@reduxjs/toolkit';

import { RootState } from '..';

import { registrationCreated } from '../actions';
import {
  B2B_PRODUCT_SUB_SYSTEM_ID,
  selectPreparationCentre,
  selectSelectedCountryId,
  setCountry,
} from '../country/countrySlice';

export interface SessionInfoState {
  sessionId: string;
  isCheckpoint: boolean;
}

export interface ExamsSearchState {
  selectedAwardingBody: string;
  selectedSession?: SessionInfoState;
  selectedProductSubSystem: string;
  selectedQualificationLevel: string;
  selectedLocation: string;
  selectedSubjects: string[];
  showAvailableExams: boolean;
}

const initialState: ExamsSearchState = {
  selectedAwardingBody: '',
  selectedSession: undefined,
  selectedProductSubSystem: '',
  selectedQualificationLevel: '',
  selectedLocation: '',
  selectedSubjects: [],
  showAvailableExams: false,
};

export const examsSearchSelectorSlice = createSlice({
  name: 'exams',
  initialState,
  reducers: {
    setAwardingBody: (state, action: PayloadAction<string>) => {
      return {
        ...initialState,
        selectedAwardingBody: action.payload
      };
    },
    setSession: (state, action: PayloadAction<[string,boolean, string]>) => {
      const [sessionId,isCheckpoint, selectedProductSubSystem] = action.payload;

      return {
        ...initialState,
        selectedAwardingBody: state.selectedAwardingBody,
        selectedProductSubSystem,
        selectedSession: {
          isCheckpoint,
          sessionId
        }
      };
    },
    setQualificationLevel: (state, action: PayloadAction<string>) => {
      state.selectedQualificationLevel = action.payload;

      state.selectedLocation = '';
      state.selectedSubjects = [];
      state.showAvailableExams = false;
    },
    setLocation: (state, action: PayloadAction<string>) => {
      state.selectedLocation = action.payload;

      state.selectedSubjects = [];
      state.showAvailableExams = false;
    },
    setSubjects: (state, action: PayloadAction<string[]>) => {
      state.selectedSubjects = [...action.payload];
    },
    setShowAvailableExams: (state) => {
      state.showAvailableExams = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(setCountry, () => initialState)
      .addCase(registrationCreated, () => initialState)
      .addDefaultCase(() => {});
  },
});

export const { setAwardingBody, setSession, setQualificationLevel, setLocation, setSubjects, setShowAvailableExams } =
  examsSearchSelectorSlice.actions;

export const selectSelectedAwardingBody = (state: RootState) => state.exams.selectedAwardingBody;

export const selectSelectedSession = (state: RootState) => state.exams.selectedSession;

export const selectSelectedProductSubSystem = (state: RootState) => state.exams.selectedProductSubSystem;

export const selectSelectedQualificationLevel = (state: RootState) => state.exams.selectedQualificationLevel;

export const selectQualificationLevelPrerequisites = createSelector(
  [
    selectSelectedAwardingBody,
    selectSelectedSession,
    selectSelectedProductSubSystem,
    selectSelectedCountryId,
    selectPreparationCentre,
  ],
  (awardingBody, session, productSubSystemId, countryId, pcInfo) => {
    const complete = awardingBody && productSubSystemId && session && countryId;

    return {
      awardingBody,
      productSubSystemId: pcInfo ? B2B_PRODUCT_SUB_SYSTEM_ID.toString() : productSubSystemId,
      session,
      countryId,
      complete,
    };
  }
);

export const selectSelectedLocation = (state: RootState) => state.exams.selectedLocation;

export const selectLocationPrerequisites = createSelector(
  [selectSelectedQualificationLevel, selectQualificationLevelPrerequisites],
  (qualificationLevel, qualificationPrerequisites) => {
    const complete = qualificationPrerequisites.complete && qualificationLevel;

    return {
      ...qualificationPrerequisites,
      qualificationLevel,
      complete,
    };
  }
);

export const selectSelectedSubjects = (state: RootState) => state.exams.selectedSubjects;

export const selectSubjectPrerequisites = createSelector(
  [
    selectSelectedAwardingBody,
    selectSelectedSession,
    selectSelectedQualificationLevel,
    selectSelectedLocation,
    selectPreparationCentre,
    selectSelectedProductSubSystem,
  ],
  (awardingBody, session, qualificationLevel, location, pcInfo, productSubSystemId) => {
    const complete = awardingBody && session && qualificationLevel && location && productSubSystemId;

    return {
      awardingBody,
      session,
      qualificationLevel,
      location,
      productSubSystemId: pcInfo ? B2B_PRODUCT_SUB_SYSTEM_ID.toString() : productSubSystemId,
      complete,
    };
  }
);

export const selectExamsSearchPrerequisites = createSelector(
  [
    selectSelectedAwardingBody,
    selectSelectedLocation,
    selectSelectedSubjects,
    selectSelectedQualificationLevel,
    selectSelectedProductSubSystem,
    selectSelectedSession,
    selectPreparationCentre,
  ],
  (awardingBodyId, districtId, examSyllabusIds, qualificationLevelId, productSubSystemId, session, pcInfo) => {
    const complete =
      awardingBodyId &&
      districtId &&
      examSyllabusIds.length > 0 &&
      qualificationLevelId &&
      productSubSystemId &&
      session;

    return {
      awardingBodyId: Number(awardingBodyId),
      districtId: Number(districtId),
      examSyllabusIds: examSyllabusIds.map(Number),
      qualificationLevelId: Number(qualificationLevelId),
      productSubSystemId: pcInfo ? B2B_PRODUCT_SUB_SYSTEM_ID : Number(productSubSystemId),
      sessionId: Number(session?.sessionId),
      complete,
      preparationCentreCode: pcInfo?.preparationCentreId,
    };
  }
);

export const selectShowAvailableExams = (state: RootState) => state.exams.showAvailableExams;

export default examsSearchSelectorSlice.reducer;
